/**
 * ReportsHeader — the admin Reports screen chrome (Claude Design "Reports"
 * handoff, part 1): segmented tabs + context fields, the period overview, and
 * the entity selector grid.
 *
 * The cards drive the existing dept/staff filters, so selecting one simply
 * scopes the report list that already exists below.
 */

import React from 'react';
import {
  INK, YELLOW, TEAL, NAVY, T4, NUM, BRAND_LOGO,
  DAYS, parseISO, longDate, isoWeekRange, money, plural,
} from './reportKit';

const G_MUTED = '#8a949c', LINE = '#e5e9ec', PILL = '#f1f3f5';

const TABS = [
  { key: 'paperwork', label: 'Paperwork (Daily)' },
  { key: 'weekly', label: 'Weekly Summary' },
  { key: 'staff', label: 'Staff Weekly' },
];

// Seal shown when a department/person has no brand logo.
const seal = (name) => name.replace(/\s*CARS$/, '').slice(0, 4).toUpperCase();

const fieldStyle = {
  height: 36, padding: '0 10px', borderRadius: 9, border: `1.5px solid ${LINE}`,
  fontSize: 13, fontWeight: 600, color: NAVY, background: '#fff', outline: 'none',
};

function Card({ label, logo, sealText, total, meta, selected, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: '#fff', border: `1.5px solid ${selected ? TEAL : LINE}`, borderRadius: 12,
      padding: '11px 13px', cursor: 'pointer',
      boxShadow: selected ? '0 0 0 3px rgba(74,122,145,.13)' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 22 }}>
        {logo
          ? <img src={logo} alt="" style={{ width: 34, height: 22, objectFit: 'contain', flex: 'none' }} />
          : <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.2, border: `1.5px solid ${INK}`, padding: '3px 5px', lineHeight: 1, flex: 'none' }}>{sealText}</span>}
        <span style={{ fontSize: 11, fontWeight: 800, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      </div>
      <div style={{ fontSize: 17, fontWeight: 800, color: INK, marginTop: 7, ...NUM }}>{money(total)}</div>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: G_MUTED, marginTop: 2, ...NUM }}>{meta}</div>
    </div>
  );
}

export default function ReportsHeader({
  mode, setMode,
  selectedDate, setSelectedDate,
  selectedWeek, setSelectedWeek, selectedYear, setSelectedYear, weekOptions, years,
  entities, selectedKey, onSelect,
  periodTotal, periodMeta, totalLabel,
  onExportAll, onPrintAll, savingPdf,
}) {
  const daily = mode === 'paperwork';
  const periodTitle = daily
    ? `${DAYS[parseISO(selectedDate).getDay()]} ${longDate(parseISO(selectedDate))}`
    : `Week ${selectedWeek} · ${selectedYear}`;
  const periodSub = daily ? '' : isoWeekRange(selectedWeek, selectedYear);

  return (
    <div className="reports-no-print">
      {/* Toolbar */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
        padding: '14px 24px', borderBottom: '1px solid #e3e7ea', background: '#fff',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ display: 'flex', gap: 3, background: PILL, padding: 3, borderRadius: 11 }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setMode(t.key)} style={{
              height: 36, padding: '0 16px', border: 'none', borderRadius: 9, cursor: 'pointer',
              fontSize: 13, fontWeight: 700,
              background: mode === t.key ? NAVY : 'transparent',
              color: mode === t.key ? '#fff' : '#5a6a74',
            }}>{t.label}</button>
          ))}
        </div>

        {daily ? (
          <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={fieldStyle} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select value={selectedWeek} onChange={e => setSelectedWeek(parseInt(e.target.value))} style={fieldStyle}>
              {weekOptions.map(w => <option key={w} value={w}>Week {w}</option>)}
            </select>
            <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))} style={fieldStyle}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            {/* The real date range — never the ISO week on its own. */}
            <span style={{ fontSize: 12.5, fontWeight: 600, color: G_MUTED, ...NUM }}>{periodSub}</span>
          </div>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={onExportAll} disabled={savingPdf} style={{
            height: 36, padding: '0 16px', borderRadius: 9, border: `1.5px solid ${LINE}`,
            background: '#fff', color: '#374151', fontSize: 13, fontWeight: 700,
            cursor: savingPdf ? 'wait' : 'pointer',
          }}>{savingPdf ? 'Saving…' : 'Export all'}</button>
          <button onClick={onPrintAll} style={{
            height: 36, padding: '0 18px', borderRadius: 9, border: 'none',
            background: TEAL, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>Print all</button>
        </div>
      </div>

      {/* Period overview */}
      <div style={{ padding: '16px 24px 0', display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 21, fontWeight: 800, color: INK, ...NUM }}>{periodTitle}</div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: G_MUTED, marginTop: 3, ...NUM }}>{periodMeta}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase', color: T4 }}>{totalLabel}</div>
          <div style={{ background: YELLOW, color: INK, fontSize: 21, fontWeight: 800, padding: '5px 13px', borderRadius: 6, marginTop: 4, ...NUM }}>
            {money(periodTotal)}
          </div>
        </div>
      </div>

      {/* Entity selector */}
      {entities.length > 0 && (
        <div style={{ padding: '14px 24px 4px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))', gap: 9 }}>
          <Card label="ALL" sealText="ALL" total={periodTotal}
            meta={plural(entities.length, mode === 'staff' ? 'person' : 'department')}
            selected={selectedKey === 'ALL'} onClick={() => onSelect('ALL')} />
          {entities.map(e => (
            <Card key={e.key} label={e.label} logo={BRAND_LOGO[e.key]} sealText={seal(e.label)}
              total={e.total} meta={e.meta}
              selected={selectedKey === e.key} onClick={() => onSelect(e.key)} />
          ))}
        </div>
      )}
    </div>
  );
}
